<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">당첨자 결제 요청 문자 발송</h5>
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted small" v-if="selectedArea && flatWinners.length > 0">
          총 {{ flatWinners.length }}명
        </span>
        <button v-if="selectedArea && flatWinners.length > 0" type="button" class="btn btn-sm btn-outline-primary" @click="toggleAllGroupsCheckAll">
          ☑️ 모두 선택
        </button>
        <button v-if="selectedArea && flatWinners.length > 0" type="button" class="btn btn-sm btn-outline-success" @click="exportToExcel">
          📥 엑셀 다운로드
        </button>
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h6 class="fw-bold mb-3">주차장 선택</h6>
        <div class="mb-3">
          <select v-model="selectedArea" class="form-select" @change="fetchWinners">
            <option value="">-- 주차장 선택 --</option>
            <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
              {{ a.AreaName }} ({{ a.AreaCode }})
            </option>
          </select>
        </div>

      </div>
    </div>

    <div v-if="selectedArea">
      <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>
      <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

      <div v-for="group in winnerGroups" :key="group.GroupSeq" class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="fw-bold mb-0">{{ group.GroupName }} ({{ group.groupWinners.length }}명)</h6>
            <div class="d-flex gap-2">
              <button v-if="group.groupWinners.length > 0" type="button" class="btn btn-sm btn-outline-info" @click="openCardSmsModal">
                💳 카드 청구서 문자
              </button>
              <button v-if="group.groupWinners.length > 0" type="button" class="btn btn-sm btn-outline-warning" @click="openCashSmsModal">
                💵 현금 청구서 문자
              </button>
            </div>
          </div>

          <div v-if="group.groupWinners.length === 0" class="text-center text-muted py-4">
            당첨자가 없습니다.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle table-sm">
              <thead class="table-dark">
                <tr>
                  <th style="width: 30px">
                    <input type="checkbox" class="form-check-input" 
                           :checked="group.groupWinners.length > 0 && group.groupWinners.every(w => checked[w.WaitingSeq])"
                           @change="toggleGroupCheckAll(group)">
                  </th>
                  <th style="width: 60px">#</th>
                  <th>순번</th>
                  <th>이름</th>
                  <th>차량번호</th>
                  <th>그룹명</th>
                  <th>전화번호</th>
                  <th>요금제</th>
                  <th>가격</th>
                  <th>할인</th>
                  <th style="width: 100px; text-align: center;">청구서</th>
                  <th style="width: 100px; text-align: center;">증빙서류</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(w, idx) in group.groupWinners" :key="w.WaitingSeq">
                  <td>
                    <input type="checkbox" class="form-check-input" v-model="checked[w.WaitingSeq]">
                  </td>
                  <td class="text-muted small">{{ idx + 1 }}</td>
                  <td class="text-muted small">{{ w.WaitingNumber }}</td>
                  <td class="fw-500">{{ w.UserName }}</td>
                  <td class="font-monospace small">{{ w.CarNumber }}</td>
                  <td>{{ w.GroupName }}</td>
                  <td class="font-monospace small">{{ w.Tel }}</td>
                  <td>{{ w.FeeName }}</td>
                  <td>{{ w.Price?.toLocaleString() || 0 }}원</td>
                  <td>{{ w.DiscountName || '없음' }}</td>
                  <td class="text-center">
                    <button class="btn btn-sm btn-outline-primary" @click="openPaymentDetail(w)" title="청구서 상세">
                      📄
                    </button>
                  </td>
                  <td class="text-center">
                    <button v-if="w.FileCount > 0" class="btn btn-sm btn-outline-info" @click="openFileViewer(w)" title="증빙서류">
                      📎 {{ w.FileCount }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!selectedArea" class="text-center text-muted py-5">
      주차장을 선택하면 당첨자 목록이 표시됩니다.
    </div>
  </div>

  <!-- 청구서 상세 모달 (컴포넌트) -->
  <PaymentDetailModal
    :isOpen="paymentDetailModal"
    :target="paymentDetailTarget"
    :payments="paymentDetailsList"
    :processing="paymentDetailProcessing"
    :error="paymentDetailError"
    @close="closePaymentDetail"
    @complete-payment="completePaymentManually"
    @delete-payment="deletePaymentRequest"
  />

  <CardInvoiceSmsModal
    :isOpen="cardSmsModalOpen"
    :selectedItems="flatWinners.filter(w => checked[w.WaitingSeq])"
    :areas="areas"
    :selectedAreaSeq="selectedArea"
    @close="cardSmsModalOpen = false"
    @bulk-sms-sent="onCardSmsSent"
  />
  <CashInvoiceSmsModal
    :isOpen="cashSmsModalOpen"
    :selectedItems="flatWinners.filter(w => checked[w.WaitingSeq])"
    :areas="areas"
    :selectedAreaSeq="selectedArea"
    @close="cashSmsModalOpen = false"
    @bulk-sms-sent="onCashSmsSent"
  />

  <FileViewerModal
    :isOpen="fileViewerModal"
    :files="fileViewerFiles"
    @close="fileViewerModal = false"
  />
</template>

<script>
import axios from 'axios'
import CardInvoiceSmsModal from '../../components/CardInvoiceSmsModal.vue'
import CashInvoiceSmsModal from '../../components/CashInvoiceSmsModal.vue'
import PaymentDetailModal from '../../components/PaymentDetailModal.vue'
import FileViewerModal from '../../components/FileViewerModal.vue'

export default {
  name: 'AdminTicketWinner',
  components: { CardInvoiceSmsModal, CashInvoiceSmsModal, PaymentDetailModal, FileViewerModal },
  data() {
    return {
      areas: [],
      selectedArea: '',
      winnerGroups: [],
      checked: {},
      cardSmsModalOpen: false,
      cashSmsModalOpen: false,
      loading: false,
      loadError: null,
      paymentDetailModal: false,
      paymentDetailTarget: null,
      paymentDetailsList: [],
      paymentDetailProcessing: false,
      paymentDetailError: null,
      fileViewerModal: false,
      fileViewerFiles: []
    }
  },
  computed: {
    flatWinners() {
      return this.winnerGroups.reduce((acc, group) => {
        return acc.concat(group.groupWinners || [])
      }, [])
    }
  },
  mounted() {
    this.initializeAreas()
  },
  methods: {
    async initializeAreas() {
      await this.fetchAreas()
      if (this.areas.length > 0) {
        this.selectedArea = this.areas[0].AreaSeq
        await this.fetchWinners()
      }
    },
    maskCarNumber(carNumber) {
      if (!carNumber) return ''
      const s = String(carNumber).trim()
      if (s.length <= 2) return s
      // 뒷자리 2자리를 ** 로 변경
      return s.slice(0, -2) + '**'
    },
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas`)
        this.areas = res.data
      } catch (e) {
        console.error('fetchAreas error:', e)
      }
    },
    async fetchWinners() {
      if (!this.selectedArea) {
        this.winnerGroups = []
        return
      }
      this.loading = true
      this.loadError = null
      this.winnerGroups = []
      this.checked = {}
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting-results?areaSeq=${this.selectedArea}`)
        const results = res.data || []
        this.winnerGroups = results.map(group => ({
          GroupSeq: group.groupSeq,
          GroupName: group.groupName,
          QuotaCount: group.quotaCount,
          groupWinners: (group.winners || []).map(w => ({
            WaitingSeq: w.WaitingSeq,
            UserName: w.UserName,
            CarNumber: w.CarNumber,
            Tel: w.Tel,
            WaitingNumber: w.WaitingNumber,
            GroupName: group.groupName,
            AreaName: this.areas.find(a => a.AreaSeq === parseInt(this.selectedArea))?.AreaName || '',
            FeeName: w.FeeName || '미정',
            Price: w.Price || 0,
            DiscountRate: w.DiscountRate || 0,
            DiscountName: w.DiscountName || '없음',
            FileCount: w.FileCount || 0
          }))
        }))
      } catch (e) {
        this.loadError = '당첨자 목록을 불러오지 못했습니다.'
        console.error('fetchWinners error:', e)
      } finally {
        this.loading = false
      }
    },
    toggleGroupCheckAll(group) {
      const allChecked = group.groupWinners.every(w => this.checked[w.WaitingSeq])
      const nextChecked = { ...this.checked }

      if (allChecked) {
        group.groupWinners.forEach(w => {
          delete nextChecked[w.WaitingSeq]
        })
      } else {
        group.groupWinners.forEach(w => {
          nextChecked[w.WaitingSeq] = true
        })
      }

      this.checked = nextChecked
    },
    toggleAllGroupsCheckAll() {
      // 모든 당첨자가 체크되었는지 확인
      const allChecked = this.flatWinners.length > 0 && this.flatWinners.every(w => this.checked[w.WaitingSeq])
      const nextChecked = { ...this.checked }

      if (allChecked) {
        // 모두 체크되어있으면 해제
        this.flatWinners.forEach(w => {
          delete nextChecked[w.WaitingSeq]
        })
      } else {
        // 모두 체크
        this.flatWinners.forEach(w => {
          nextChecked[w.WaitingSeq] = true
        })
      }

      this.checked = nextChecked
    },
    openCardSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 당첨자를 선택하세요.')
        return
      }
      this.cardSmsModalOpen = true
    },
    openCashSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 당첨자를 선택하세요.')
        return
      }
      this.cashSmsModalOpen = true
    },
    async openPaymentDetail(w) {
      this.paymentDetailTarget = w
      this.paymentDetailsList = []
      this.paymentDetailError = null
      this.paymentDetailModal = true

      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${w.WaitingSeq}`)
        this.paymentDetailsList = res.data || []
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '청구서 조회 실패'
      }
    },
    closePaymentDetail() {
      this.paymentDetailModal = false
      this.paymentDetailTarget = null
      this.paymentDetailsList = []
      this.paymentDetailError = null
    },
    async completePaymentManually(payment) {
      this.paymentDetailProcessing = true
      this.paymentDetailError = null

      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/complete-manual`, {
          paymentSeq: payment.PaymentSeq,
          waitingSeq: this.paymentDetailTarget.WaitingSeq
        })

        this.closePaymentDetail()
        await this.fetchWinners()
        alert('결제 처리가 완료되었습니다.')
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '결제 완료 처리 실패'
      } finally {
        this.paymentDetailProcessing = false
      }
    },
    async deletePaymentRequest(payment) {
      this.paymentDetailProcessing = true
      this.paymentDetailError = null

      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/${payment.PaymentSeq}`)
        this.closePaymentDetail()
        await this.fetchWinners()
        alert('청구서가 삭제되었습니다.')
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '청구서 삭제에 실패했습니다.'
      } finally {
        this.paymentDetailProcessing = false
      }
    },
    getCheckedCount() {
      return Object.values(this.checked).filter(v => v).length
    },
    onCardSmsSent() {
      this.fetchWinners()
    },
    onCashSmsSent() {
      this.fetchWinners()
    },
    async openFileViewer(w) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting/files?waitingSeq=${w.WaitingSeq}`)
        const baseUrl = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
        this.fileViewerFiles = (res.data.files || []).map(f => {
          const url = f.url || ''
          const normalizedUrl = url.startsWith('/') ? `${baseUrl}${url}` : url
          return {
            ...f,
            url: normalizedUrl
          }
        })
        this.fileViewerModal = true
      } catch (e) {
        console.error('openFileViewer error:', e)
        alert('증빙서류를 불러오지 못했습니다: ' + (e.response?.data?.message || e.message))
      }
    },
    exportToExcel() {
      if (this.flatWinners.length === 0) {
        alert('다운로드할 당첨자가 없습니다.');
        return;
      }

      const escapeXml = value => {
        const str = String(value || '');
        return str.replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
      };

      const areaName = this.areas.find(a => a.AreaSeq === parseInt(this.selectedArea))?.AreaName || '주차장';
      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `당첨자목록_${areaName}_${timestamp}.xls`;

      const cols = [
        { width: 50 },
        { width: 100 },
        { width: 100 },
        { width: 100 },
        { width: 120 },
        { width: 100 },
        { width: 100 },
        { width: 100 },
        { width: 100 },
        { width: 100 },
        { width: 100 }
      ];

      const headers = ['순번', '신청번호', '이름', '차량번호', '전화번호', '그룹명', '요금제', '가격', '할인명', '할인율(%)', '주차장'];

      const rows = this.flatWinners.map((w, idx) => [
        idx + 1,
        w.WaitingNumber || '',
        w.UserName || '',
        w.CarNumber || '',
        w.Tel || '',
        w.GroupName || '',
        w.FeeName || '',
        w.Price ? w.Price.toLocaleString() : 0,
        w.DiscountName || '없음',
        w.DiscountRate || 0,
        w.AreaName || areaName
      ]);

      const xml = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:html="http://www.w3.org/TR/REC-html40">
  <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
    <Author>EFPark</Author>
    <Created>${new Date().toISOString()}</Created>
    <Version>16.00</Version>
  </DocumentProperties>
  <Styles>
    <Style ss:ID="Header">
      <Font ss:Bold="1"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="Default">
      <Alignment ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="당첨자">
    <Table>
      ${cols.map(col => `<Column ss:Width="${col.width}"/>`).join('')}
      <Row ss:StyleID="Header">
        ${headers.map(h => `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('')}
      </Row>
      ${rows.map(row => `
      <Row>
        ${row.map(cell => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}
      </Row>
      `).join('')}
    </Table>
  </Worksheet>
</Workbook>`;

      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 0.375rem;
  padding: 1.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 500px;
}

.table { font-size: 0.875rem; }
.fw-500 { font-weight: 500; }
.font-monospace { font-family: 'Courier New', monospace; }
</style>
